import * as DM from "@radix-ui/react-dropdown-menu";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Icon } from "../icons/Icon";
import { Avatar } from "../primitives/Avatar";
import { Button } from "../primitives/Button";
import { Menu, type MenuItem } from "../primitives/Menu";
import { Tabs } from "../primitives/Misc";
import { AttachmentCard, AttachmentList } from "./Attachments"; // the attachments round: a message's pictures open the viewer
import { cx, formatDate } from "../utils/format";
import type { Agent, Attachment, ChatMessage, MentionChip, TicketChip } from "../types";

const LOCALE = "en";
const timeOf = (iso: string) => new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso));
const dayOf = (iso: string) => new Intl.DateTimeFormat(LOCALE, { weekday: "long", day: "numeric", month: "long" }).format(new Date(iso));
const dayKey = (iso: string) => new Date(iso).toDateString();

export interface TicketRef { href: string; label: string; }

export const EMOJI = ["👍", "🙌", "👀", "✅", "❤️", "🔥", "😂", "🎉", "👏", "🤔", "😅", "🙏", "💯", "🚀", "😍", "🤷", "👋", "✨", "🐛", "⭐", "😢", "💡", "🙈", "🫡"];

/** A small emoji grid in a menu; `trigger` is the button that opens it. */
export function EmojiPicker({ trigger, onPick, align = "start" }: { trigger: ReactNode; onPick: (emoji: string) => void; align?: "start" | "end" }) {
  return (
    <DM.Root>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content className="ob-menu ob-emoji" sideOffset={6} align={align} collisionPadding={12}>
          {EMOJI.map((e) => <DM.Item key={e} className="ob-emoji__item" onSelect={() => onPick(e)}>{e}</DM.Item>)}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

/** The channel's bar: what to look at on the left, who is here on the right. It is as tall as the band over the channel list, so its
 *  hairline continues that one. The name and what the channel is for live in the breadcrumb; here they are only the section's heading.
 *  The ticket discussion round (2026-11-02): a ticket's discussion puts its own lead in the title's place (the link to the ticket and its
 *  status) and `actions` beside the people (Follow / Stop following). */
export function ChatHeader({ name, kind, members, addPeople, tab = "messages", onTab, lock, lead, actions }: {
  name: string; kind: "channel" | "dm" | "ticket"; members: Agent[]; addPeople?: ReactNode; /** left out (with `onTab`): no tabs, the room has messages only */ tab?: string; onTab?: (t: string) => void;
  /** a private channel: a lock and the word before the people */ lock?: boolean;
  /** in the title's place: what the room is about (a ticket's link and status) */ lead?: ReactNode; /** after the people: the room's own buttons */ actions?: ReactNode;
}) {
  return (
    <div className="ob-chat__head">
      <h1 className="ob-sr-only">{kind === "channel" ? `#${name}` : name}</h1>
      <div className="ob-chat__bar">
        {onTab ? <Tabs variant="line" items={[{ id: "messages", label: "Messages" }, { id: "files", label: "Files & links" }]} active={tab} onChange={onTab} /> : lead ? <div className="ob-chat__lead">{lead}</div> : <span className="ob-chat__bar-title">Messages</span>}
        <div className="ob-chat__people">
          {lock && <span className="ob-chat__lock" title="Private: only its people see it"><Icon name="lock" size="sm" /><span className="ob-chat__lock-text">Private</span></span>}
          <div className="ob-chat__members" title={members.map((m) => m.name).join(", ")}>
            {members.slice(0, 5).map((m) => <Avatar key={m.id} size="sm" initials={m.initials} src={m.photo} className={cx("ob-chat__member", m.online && "ob-chat__member--online")} />)}
            <span className="ob-chat__member-count">{members.length}</span>
          </div>
          {kind === "channel" && addPeople}
          {actions}
        </div>
      </div>
    </div>
  );
}

/** Body text with URLs as links, #1234 as ticket chips and @Name as mention chips. Given `tickets` or `mentions` (M10: what the server
 *  parsed and checked), only those are drawn, whatever the number of digits or words: a listed `#n` is a chip (a link when it has an
 *  `href`, plain text when the reader may not see it), a listed person's `@Name` one mention; anything else stays text. Without them the
 *  text is read by pattern, as the prototype does. */
export function MessageBody({ text, resolveTicket, people, tickets, mentions }: { text: string; resolveTicket?: (n: number) => TicketRef | null; people?: string[]; tickets?: TicketChip[]; mentions?: MentionChip[] }) {
  if (tickets || mentions) return <ListedBody text={text} tickets={tickets ?? []} mentions={mentions ?? []} />;
  const parts = text.split(/(https?:\/\/\S+|#\d{3,6}|@[\w-]+)/g);
  return (
    <span>
      {parts.map((part, i) => {
        if (/^https?:\/\//.test(part)) return <a key={i} href={part} className="ob-chat__link" target="_blank" rel="noreferrer">{part.replace(/^https?:\/\//, "")}</a>;
        const t = part.match(/^#(\d{3,6})$/);
        if (t) { const ref = resolveTicket?.(Number(t[1])); return ref ? <a key={i} href={ref.href} className="ob-chat__ticket"><Icon name="inbox" size="xs" />{ref.label}</a> : <span key={i}>{part}</span>; }
        const m = part.match(/^@([\w-]+)$/);
        if (m && people?.some((n) => n.toLowerCase() === m[1]!.toLowerCase())) return <span key={i} className="ob-chat__mention">{part}</span>;
        return <span key={i}>{part.split("\n").map((l, j) => <span key={j}>{j > 0 && <br />}{l}</span>)}</span>;
      })}
    </span>
  );
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const lines = (part: string) => part.split("\n").map((l, j) => <span key={j}>{j > 0 && <br />}{l}</span>);
/** the text cut by the server's lists: links, the listed tickets (standing alone, as the server finds them) and the listed people's names */
function ListedBody({ text, tickets, mentions }: { text: string; tickets: TicketChip[]; mentions: MentionChip[] }) {
  const alts = ["https?:\\/\\/\\S+"];
  if (tickets.length) alts.push(`(?<=^|[\\s([{])#(?:${tickets.map((t) => t.number).join("|")})(?![\\p{L}\\p{N}_])`);
  if (mentions.length) alts.push(`@(?:${[...mentions].sort((a, b) => b.name.length - a.name.length).map((m) => escapeRe(m.name)).join("|")})(?![\\p{L}\\p{N}_])`);
  const parts = text.split(new RegExp(`(${alts.join("|")})`, "gu"));
  return (
    <span>
      {parts.map((part, i) => {
        if (i % 2 === 0) return <span key={i}>{lines(part)}</span>;
        if (/^https?:\/\//.test(part)) return <a key={i} href={part} className="ob-chat__link" target="_blank" rel="noreferrer">{part.replace(/^https?:\/\//, "")}</a>;
        if (part.startsWith("#")) { const t = tickets.find((x) => x.number === Number(part.slice(1))); return t?.href ? <a key={i} href={t.href} className="ob-chat__ticket"><Icon name="inbox" size="xs" />{t.label}</a> : <span key={i}>{t?.label ?? part}</span>; }
        return <span key={i} className="ob-chat__mention">{part}</span>;
      })}
    </span>
  );
}

/** a message past this many characters is folded behind "Show more" (M10, choice 10: a long paste does not eat the room) */
export const CHAT_FOLD_AT = 4_000;
/** the shown part of a long text: cut at the last white space before the limit */
const folded = (text: string, at: number) => { const cut = text.lastIndexOf(" ", at); return `${text.slice(0, cut > at * 0.8 ? cut : at).trimEnd()}…`; };

const whoText = (who: string[], agents: Agent[], me: string, emoji: string) => {
  const names = who.map((id) => (id === me ? "You" : agents.find((x) => x.id === id)?.name ?? id));
  const list = names.length <= 2 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${list} reacted with ${emoji}`;
};

/** What the reader may do to a message, decided by the app: the ⋯ menu offers Edit and Delete where the checks pass. An edit saves the
 *  new text; a delete leaves the message in its place as "Message deleted". */
export interface MessageActions {
  canEdit: (m: ChatMessage) => boolean;
  canDelete: (m: ChatMessage) => boolean;
  onEdit: (id: string, body: string) => void;
  onDelete: (id: string) => void;
}

export interface MessageItemProps {
  m: ChatMessage; agents: Agent[]; me: string; grouped?: boolean; resolveTicket?: (n: number) => TicketRef | null;
  /** left out, the message takes no reactions (a read-only room) */ onReact?: (id: string, emoji: string) => void; onReply?: (id: string) => void;
  replies?: ChatMessage[]; onOpenThread?: (id: string) => void;
  /** left out, the message has no ⋯ menu */ actions?: MessageActions;
}

/** One message: header, body, attachments, permanent reactions with an add button, a hover action bar, and the thread summary.
 *  A deleted message keeps its place and says so; an edited one carries a quiet "edited". Editing happens in place. */
export function ChatMessageItem({ m, agents, me, grouped, resolveTicket, onReact, onReply, replies = [], onOpenThread, actions }: MessageItemProps) {
  const [mode, setMode] = useState<"read" | "edit" | "delete">("read");
  const [open, setOpen] = useState(false);
  const long = m.body.length > CHAT_FOLD_AT;
  const a = agents.find((x) => x.id === m.authorId);
  const people = agents.map((x) => x.name);
  const deleted = !!m.deletedAt;
  const reactions = deleted ? [] : Object.entries(m.reactions).filter(([, who]) => who.length > 0);
  const last = replies[replies.length - 1];
  const repliers = [...new Set(replies.map((r) => r.authorId))].slice(0, 3).map((id) => agents.find((x) => x.id === id)).filter(Boolean) as Agent[];
  const menu: MenuItem[] = !actions || deleted ? [] : [
    ...(actions.canEdit(m) ? [{ id: "edit", label: "Edit", icon: "pen" as const, onSelect: () => setMode("edit") }] : []),
    ...(actions.canDelete(m) ? [{ id: "delete", label: "Delete", icon: "trash" as const, onSelect: () => setMode("delete") }] : []),
  ];
  const bar = !deleted && mode === "read" && (onReact || onReply || menu.length > 0);
  return (
    <div className={cx("ob-msg", grouped && "ob-msg--grouped", mode === "edit" && "ob-msg--editing")}>
      <div className="ob-msg__gutter">{grouped ? <span className="ob-msg__time-hover">{timeOf(m.createdAt)}</span> : <Avatar initials={a?.initials ?? "?"} src={a?.photo} className={cx(a?.online && "ob-chat__member--online")} />}</div>
      <div className="ob-msg__main">
        {!grouped && <div className="ob-msg__head"><span className="ob-msg__author">{a?.name ?? "Unknown"}{m.via && <> · via <span className="ob-msg__via" title={m.via.title}>{m.via.app}</span></>}{m.authorId === me && <span className="ob-msg__you"> (you)</span>}</span><span className="ob-msg__time">{timeOf(m.createdAt)}</span></div>}
        {deleted ? <div className="ob-msg__body ob-msg__body--deleted">Message deleted</div>
          : mode === "edit" && actions ? <MessageEditor initial={m.body} onSave={(body) => { actions.onEdit(m.id, body); setMode("read"); }} onCancel={() => setMode("read")} />
          : <div className="ob-msg__body"><MessageBody text={long && !open ? folded(m.body, CHAT_FOLD_AT) : m.body} resolveTicket={resolveTicket} people={people} tickets={m.tickets} mentions={m.mentions} />{m.editedAt && <span className="ob-msg__edited" title={`Edited ${timeOf(m.editedAt)}`}>(edited)</span>}
            {long && <button type="button" className="ob-msg__more" aria-expanded={open} onClick={() => setOpen((x) => !x)}>{open ? "Show less" : "Show more"}</button>}</div>}
        {mode === "delete" && actions && (
          <div className="ob-msg__confirm" role="group" aria-label="Delete message">
            <span className="ob-msg__confirm-text">Delete this message for everyone?</span>
            <Button size="sm" variant="primary" onClick={() => { actions.onDelete(m.id); setMode("read"); }}>Delete</Button>
            <Button size="sm" onClick={() => setMode("read")}>Cancel</Button>
          </div>
        )}
        {!deleted && m.attachments && m.attachments.length > 0 && <AttachmentList items={m.attachments} className="ob-msg__attachments" />}
        {reactions.length > 0 && (
          <div className="ob-msg__reactions">
            {reactions.map(([emoji, who]) => (
              <button type="button" key={emoji} className={cx("ob-reaction", who.includes(me) && "ob-reaction--mine")} aria-label={whoText(who, agents, me, emoji)} data-balloon-pos="up-left" disabled={!onReact} onClick={() => onReact?.(m.id, emoji)}>{emoji} {who.length}</button>
            ))}
            {onReact && <EmojiPicker onPick={(e) => onReact(m.id, e)} trigger={<button type="button" className="ob-reaction ob-reaction--add" aria-label="Add reaction"><Icon name="smile" size="sm" /><Icon name="plus" size="xs" /></button>} />}
          </div>
        )}
        {replies.length > 0 && onOpenThread && (
          <button type="button" className="ob-msg__thread" onClick={() => onOpenThread(m.id)}>
            <span className="ob-msg__thread-avatars">{repliers.map((r) => <Avatar key={r.id} size="sm" initials={r.initials} src={r.photo} />)}</span>
            <span className="ob-msg__thread-count">{replies.length} {replies.length === 1 ? "reply" : "replies"}</span>
            {last && <span className="ob-msg__thread-last">Last reply {timeOf(last.createdAt)}</span>}
          </button>
        )}
      </div>
      {bar && (
        <div className="ob-msg__actions">
          {onReact && <EmojiPicker align="end" onPick={(e) => onReact(m.id, e)} trigger={<button type="button" className="ob-msg__action" aria-label="Add reaction" data-balloon-pos="up-right"><Icon name="smile" size="sm" /></button>} />}
          {onReply && <button type="button" className="ob-msg__action" aria-label="Reply in thread" data-balloon-pos="up-right" onClick={() => onReply(m.id)}><Icon name="chat" size="sm" /></button>}
          {menu.length > 0 && <Menu align="end" sections={[{ items: menu }]} trigger={<button type="button" className="ob-msg__action" aria-label="Message actions" data-balloon-pos="up-right"><Icon name="more" size="sm" /></button>} />}
        </div>
      )}
    </div>
  );
}

/** The message's text, editable in place: Enter saves, Shift+Enter is a new line, Escape (or Cancel) leaves it as it was. An unchanged or
 *  empty text saves nothing. */
function MessageEditor({ initial, onSave, onCancel }: { initial: string; onSave: (body: string) => void; onCancel: () => void }) {
  const [text, setText] = useState(initial);
  const save = () => { const body = text.trim(); if (!body) return; if (body === initial.trim()) onCancel(); else onSave(body); };
  return (
    <div className="ob-msg__edit">
      <textarea className="ob-msg__edit-input" value={text} aria-label="Edit message" autoFocus rows={Math.min(8, text.split("\n").length + 1)}
        onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); onCancel(); } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); save(); } }} />
      <div className="ob-msg__edit-foot">
        <span className="ob-msg__edit-hint">Enter to save · Escape to cancel</span>
        <Button size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" variant="primary" disabled={!text.trim()} onClick={save}>Save</Button>
      </div>
    </div>
  );
}

/** A line of the room itself between the messages (2026-10-07: a private channel archived by a manager outside it says so here): quiet,
 *  centred, no author or menu, so it never reads as someone's message. */
export function ChatSystemLine({ text }: { text: string }) {
  return <div className="ob-chat__system" role="note"><span className="ob-chat__system-text">{text}</span></div>;
}

/** One line where the composer would be, for a room nobody can write in (an archived channel); `action` is a button beside it (Restore). */
export function ChatReadOnly({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="ob-chat__readonly" role="status"><span className="ob-chat__readonly-text">{children}</span>{action}</div>;
}

/** A chat stays at its newest message when its box changes size (the window, the composer growing, a thread opening),
 *  unless the reader has scrolled up: then what they are reading stays where it is. */
function usePinnedToEnd(ref: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let atEnd = true;
    // "at the end" includes the list's own bottom padding, which scrolling the last message into view leaves below it
    const onScroll = () => { atEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 48; };
    const resized = new ResizeObserver(() => { if (atEnd) el.scrollTop = el.scrollHeight; });
    el.addEventListener("scroll", onScroll, { passive: true });
    resized.observe(el);
    return () => { el.removeEventListener("scroll", onScroll); resized.disconnect(); };
  }, [ref]);
}

export function ChatMessageList({ messages, agents, me, resolveTicket, onReact, onReply, repliesOf, onOpenThread, empty, intro, typing, actions, narrow }: {
  messages: ChatMessage[]; agents: Agent[]; me: string; resolveTicket?: (n: number) => TicketRef | null; /** left out: no reactions (a read-only room) */ onReact?: (id: string, emoji: string) => void;
  /** the ⋯ menu's Edit and Delete, where the app allows them */ actions?: MessageActions;
  onReply?: (id: string) => void; repliesOf?: (id: string) => ChatMessage[]; onOpenThread?: (id: string) => void; empty?: ReactNode;
  /** one quiet line above the first message: what the channel is for, where the top bar has no room to say it (phones) */ intro?: ReactNode;
  /** the name of someone writing in this room right now: one line under the last message */ typing?: string;
  /** the ticket discussion round (2026-11-02): the narrow chat of a side panel, which scrolls its own list to the newest message and never the
   *  page or the ticket's thread around it (`scrollIntoView` would move every scrolling box above it) */ narrow?: boolean;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (narrow) { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; } else endRef.current?.scrollIntoView({ block: "end" }); }, [messages.length, typing, narrow]);
  usePinnedToEnd(listRef);
  const listClass = cx("ob-chat__list", narrow && "ob-chat__list--narrow");
  if (messages.length === 0) return <div className={listClass}>{intro}{empty}<Typing name={typing} /></div>;
  let lastDay = ""; let lastAuthor = ""; let lastTime = 0;
  return (
    <div className={listClass} ref={listRef}>
      {intro}
      {messages.map((m) => {
        const day = dayKey(m.createdAt);
        const t = new Date(m.createdAt).getTime();
        const showDay = day !== lastDay;
        const author = `${m.authorId}|${m.via?.app ?? ""}`; // the discussion polish round (2026-11-02): a member's AI line never hides under its member's header
        const grouped = !showDay && author === lastAuthor && t - lastTime < 5 * 60_000; // same author within five minutes: no repeated header
        lastDay = day; lastAuthor = m.system ? "" : author; lastTime = t; // the message after a system line shows its author again
        return (
          <div key={m.id}>
            {showDay && <div className="ob-chat__day"><span className="ob-chat__day-pill">{dayOf(m.createdAt)}</span></div>}
            {m.system ? <ChatSystemLine text={m.body} /> : <ChatMessageItem m={m} agents={agents} me={me} grouped={grouped} resolveTicket={resolveTicket} onReact={onReact} onReply={onReply} replies={repliesOf?.(m.id)} onOpenThread={onOpenThread} actions={actions} />}
          </div>
        );
      })}
      <Typing name={typing} />
      <div ref={endRef} />
    </div>
  );
}

/** "Yigit is typing": a quiet line where the next message will appear. Words, no animation. */
function Typing({ name }: { name?: string }) {
  return name ? <div className="ob-chat__typing" role="status">{name} is typing…</div> : null;
}

/** The thread panel: the parent message, its replies, and a composer passed in by the app. */
export function ThreadPanel({ parent, replies, agents, me, resolveTicket, onReact, onClose, composer, channelLabel, typing, actions }: {
  parent: ChatMessage; replies: ChatMessage[]; agents: Agent[]; me: string; resolveTicket?: (n: number) => TicketRef | null; onReact?: (id: string, emoji: string) => void; onClose: () => void; composer: ReactNode; channelLabel: string; typing?: string; actions?: MessageActions;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [replies.length, typing]);
  return (
    <aside className="ob-thread" aria-label="Thread">
      <div className="ob-thread__head"><span className="ob-thread__title">Thread</span><span className="ob-thread__where">{channelLabel}</span><Button variant="icon" icon="x" title="Close thread" onClick={onClose} className="ob-thread__close" /></div>
      <div className="ob-thread__list">
        <ChatMessageItem m={parent} agents={agents} me={me} resolveTicket={resolveTicket} onReact={onReact} actions={actions} />
        {replies.length > 0 && <div className="ob-thread__sep"><span>{replies.length} {replies.length === 1 ? "reply" : "replies"}</span></div>}
        {replies.map((r, i) => <ChatMessageItem key={r.id} m={r} agents={agents} me={me} resolveTicket={resolveTicket} onReact={onReact} actions={actions} grouped={i > 0 && replies[i - 1]!.authorId === r.authorId && new Date(r.createdAt).getTime() - new Date(replies[i - 1]!.createdAt).getTime() < 5 * 60_000} />)}
        <Typing name={typing} />
        <div ref={endRef} />
      </div>
      {composer}
    </aside>
  );
}

export function FilesAndLinks({ items }: { items: { attachment: Attachment; authorName: string; createdAt: string }[] }) {
  if (items.length === 0) return <div className="ob-chat__list"><div className="ob-empty"><div><div className="ob-empty__title">No files or links yet</div><div className="ob-empty__text">Anything shared in this conversation shows up here.</div></div></div></div>;
  return (
    <div className="ob-chat__list ob-chat__files">
      {items.map((it, i) => (
        <div className="ob-file-row" key={i}>
          <AttachmentCard a={it.attachment} />
          <span className="ob-file-row__meta">{it.authorName} · {formatDate(it.createdAt)}</span>
        </div>
      ))}
    </div>
  );
}
